import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../utils/api.js';
import { useAuth } from '../../context/AuthContext.jsx';
import StatusBadge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import Card from '../../components/ui/Card.jsx';
import { formatIST, formatDate } from '../../utils/formatters.js';

export default function DeliveryNoteListPage() {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['delivery-notes', page],
    queryFn: () => api.get('/delivery-notes', { params: { page, limit: 20 } }).then(r => r.data.data),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Delivery Notes</h1>
          <p className="text-sm text-gray-500 mt-0.5">{data?.total ?? '—'} total records</p>
        </div>
        <Link to="/delivery-notes/new"><Button icon={Plus}>New Delivery Note</Button></Link>
      </div>
      <Card>
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['DN Number','Contents','From','To','Expected','Status',''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase tracking-wide">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.items || []).map(dn => {
                  const isOverdue = dn.status !== 'DELIVERED' && new Date(dn.expectedDeliveryAt) < new Date();
                  return (
                    <tr key={dn.id} className={"hover:bg-gray-50" + (isOverdue ? " bg-orange-50" : "")}>
                      <td className="px-4 py-3">
                        <Link to={`/delivery-notes/${dn.id}`} className="font-mono font-semibold text-teal-700 hover:underline">{dn.number}</Link>
                      </td>
                      <td className="px-4 py-3 max-w-xs truncate">{dn.contentDescription}</td>
                      <td className="px-4 py-3 text-gray-600">{dn.sender?.name}</td>
                      <td className="px-4 py-3 text-gray-600">{dn.receiver?.name || dn.receiverText || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        <span className={isOverdue ? 'text-orange-600 font-medium' : ''}>{formatIST(dn.expectedDeliveryAt, 'dd MMM, hh:mm a')}</span>
                      </td>
                      <td className="px-4 py-3">
                        {isOverdue && dn.status !== 'DELIVERED' ? (
                          <span className="badge badge-overdue">Overdue</span>
                        ) : <StatusBadge status={dn.status} />}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/delivery-notes/${dn.id}`} className="text-xs text-teal-600 hover:underline">View →</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
